





























































































































































































































































































































































































import { Component, Prop, Vue } from 'vue-property-decorator';
import MaterialCard from '@/components/material/Card/index.vue';
import { AppModule } from '@/store/modules/app';
import { UserModule } from '@/store/modules/user';
import {
  IGroup,
  IPlanSummaryData,
  PlanUnit,
  BookingTypeEnum,
  TicketSetting,
  Permission,
  TicketPermission,
  BookingPermission,
  AdminPermission,
  TicketPermissionAllowedActionEnum,
  BookingPermissionAllowedActionEnum,
  AdminPermissionAllowedActionEnum,
  IUserTokenData,
} from '@/api/types';
import {
  IPutGroupRequest,
  putGroup,
  IListGroupsRequest,
  listGroups,
} from '@/api/users';
import {
  IListTicketSettingsRequest,
  listTicketSettings,
  IListPlansRequest,
  listPlans,
} from '@/api/tickets';
import { formatApiError, compareString } from '@/utils/index';
import { planToText, ticketToText, TicketType } from '@/utils/tickets';
import { translateRouteTitle } from '@/utils/permission';

type ItemType = {
  text: string;
  value: string;
};

type WithPermissions = {
  ticketPermissions: TicketPermission[];
  bookingPermissions: BookingPermission[];
  adminPermissions: AdminPermission[];
};

type WithIsActive = {
  isActive: boolean;
};

@Component({
  name: 'GroupCreate',
  components: {
    MaterialCard,
  },
})
export default class extends Vue {
  private loading = true;
  private valid = false;
  private editingGroup = {} as IPutGroupRequest &
    WithPermissions &
    WithIsActive;
  private ticketSettingsByTicketId: Map<string, TicketSetting> = new Map();
  private ticketItems: ItemType[] = [];
  private ticketTextByTicketId: Map<string, string> = new Map();
  private plansByPlanId: Map<string, IPlanSummaryData> = new Map();
  private planUnitByPlanIdAndUnitId: Map<
    string,
    Map<string, PlanUnit>
  > = new Map();
  private redirectAfterCreate = true;
  private showRedirectAfterCreate = true;
  private dialog = false;
  private dialogConfirmHandler = this.resetDialog;
  private dialogTextTitle = '';
  private dialogTextFooter = '';
  private showCopyGroups = false;
  private canCopyGroups = false;
  private copyGroupId = '';
  private groups: Map<string, IGroup> = new Map();
  private groupItems: ItemType[] = [];

  get isDialogFullscreen() {
    return false && this.$vuetify.breakpoint.smAndDown;
  }

  get ticketPermissionAllowedActionItems() {
    return Object.values(TicketPermissionAllowedActionEnum)
      .filter(
        p =>
          (p as TicketPermissionAllowedActionEnum) !==
          TicketPermissionAllowedActionEnum.UNKNOWN
      )
      .map(p => ({
        text: this.$t(`TicketPermissionAllowedActionEnum.${p}`),
        value: p as string,
      }));
  }

  get bookingPermissionAllowedActionItems() {
    return Object.values(BookingPermissionAllowedActionEnum)
      .filter(
        p =>
          (p as BookingPermissionAllowedActionEnum) !==
          BookingPermissionAllowedActionEnum.UNKNOWN
      )
      .map(p => ({
        text: this.$t(`BookingPermissionAllowedActionEnum.${p}`),
        value: p as string,
      }));
  }

  get adminPermissionAllowedActionItems() {
    return Object.values(AdminPermissionAllowedActionEnum)
      .filter(
        p =>
          (p as AdminPermissionAllowedActionEnum) !==
          AdminPermissionAllowedActionEnum.UNKNOWN
      )
      .map(p => ({
        text: this.$t(`AdminPermissionAllowedActionEnum.${p}`),
        value: p as string,
      }));
  }

  get canCreate() {
    return this.valid;
  }

  created() {
    this.fetchData();
    this.reset();
  }

  private async fetchData(noLoading = false) {
    if (!noLoading) {
      this.loading = true;
      AppModule.StartProgress();
    }

    await Promise.all([
      this.fetchThenLoadGroups(),
      this.fetchThenLoadPlans(),
      this.fetchThenLoadTicketSetting(),
    ]);
    this.loadTicketItems();

    if (!noLoading) {
      this.loading = false;
      AppModule.StopProgress();
    }
  }

  private async fetchThenLoadPlans() {
    const res = await listPlans({});
    this.plansByPlanId = new Map(res.plans.map(p => [p.planId, p]));

    const planItems: ItemType[] = [];
    this.plansByPlanId.forEach(p => {
      planItems.push({
        text: planToText(this.$i18n, p),
        value: p.planId,
      });

      if (!this.planUnitByPlanIdAndUnitId.has(p.planId)) {
        this.planUnitByPlanIdAndUnitId.set(p.planId, new Map());
      }
      this.planUnitByPlanIdAndUnitId.set(p.planId, new Map());
    });

    this.loadUnits();
  }

  private loadUnits() {
    this.planUnitByPlanIdAndUnitId = new Map();
    this.plansByPlanId.forEach(p => {
      if (!this.planUnitByPlanIdAndUnitId.has(p.planId)) {
        this.planUnitByPlanIdAndUnitId.set(p.planId, new Map());
      }
      const units = this.planUnitByPlanIdAndUnitId.get(p.planId)!;
      p.units.forEach(u => units.set(u.id, u));
    });
  }

  private async fetchThenLoadTicketSetting() {
    try {
      const res = await listTicketSettings({});
      this.ticketSettingsByTicketId = new Map(res.tickets.map(t => [t.id!, t]));
    } catch (err) {
      this.$toast.error(
        this.$t('Failed to {do} due to {err}', {
          do: (this.$t('Fetch {msg}', {
            msg: this.$t('Ticket setting'),
          }) as string).toLowerCase(),
          err: formatApiError(this.$i18n, err),
        }) as string,
        {
          icon: 'mdi-alert',
        }
      );
    }
  }

  private loadTicketItems() {
    const ticketItems: ItemType[] = [];
    this.ticketSettingsByTicketId.forEach(t => {
      const plan = this.plansByPlanId.get(t.planId)! || {
        activityId: '',
        activityTitle: '-',
        planId: t.planId,
        planTitle: '-',
        bookingType: BookingTypeEnum.EACH_TIME,
      };
      const unit =
        this.planUnitByPlanIdAndUnitId.has(t.planId) &&
        this.planUnitByPlanIdAndUnitId.get(t.planId)!.has(t.unitId)
          ? this.planUnitByPlanIdAndUnitId.get(t.planId)!.get(t.unitId)!
          : {
              id: t.unitId,
              title: '-',
            };
      const ticket: TicketType = {
        activityId: plan.activityId,
        activityTitle: plan.activityTitle,
        planId: plan.planId,
        planTitle: plan.planTitle,
        unitId: unit.id,
        unitTitle: unit.title,
        bookingType: plan.bookingType || BookingTypeEnum.EACH_TIME,
        ticketId: t.id!,
        ticketTitle: t.title,
      };
      ticketItems.push({
        text: ticketToText(this.$i18n, ticket),
        value: t.id!,
      });
    });
    this.ticketItems = ticketItems.sort((a, b) =>
      compareString(a.text, b.text)
    );
    this.ticketTextByTicketId = new Map(
      ticketItems.map(t => [t.value, t.text])
    );
  }

  private resetDialog() {
    this.dialog = false;
    this.dialogTextTitle = '';
    this.dialogTextFooter = '';
    this.showRedirectAfterCreate = true;
    this.showCopyGroups = false;
    this.$nextTick(() => {
      this.dialogConfirmHandler = this.resetDialog;
    });
  }

  private showDialogForCreate() {
    this.dialogTextTitle = `${this.translateRouteTitle(
      this.$route.meta.title
    )}`;
    this.dialogTextFooter = `${this.$t('Is it OK?')}`;
    this.showRedirectAfterCreate = true;
    this.showCopyGroups = false;
    this.dialogConfirmHandler = () => {
      this.resetDialog();
      this.createGroup();
    };
    this.dialog = true;
  }

  private showDialogForReset() {
    this.dialogTextTitle = `${this.$t('Reset')}`;
    this.dialogTextFooter = `${this.$t('Is it OK?')}`;
    this.showRedirectAfterCreate = false;
    this.showCopyGroups = false;
    this.dialogConfirmHandler = () => {
      this.resetDialog();
      this.reset();
    };
    this.dialog = true;
  }

  private async showDialogForCopy() {
    this.dialogTextTitle = `${this.$t('Copy from other {msg}', {
      msg: this.$t('Group'),
    })}`;
    this.dialogTextFooter = `${this.$t('Is it OK?')}`;
    this.showRedirectAfterCreate = false;
    this.showCopyGroups = true;
    this.dialogConfirmHandler = () => {
      this.resetDialog();
      this.copyGroup();
    };
    this.dialog = true;
  }

  private async fetchThenLoadGroups() {
    try {
      const res = await listGroups({});
      this.groups = new Map(res.groups.map(g => [g.id!, g]));
      this.groupItems = res.groups.map(g => ({
        text: g.name,
        value: g.id!,
      }));
    } catch (err) {
      this.$toast.error(
        this.$t('Failed to {do} due to {err}', {
          do: (this.$t('Fetch {msg}', {
            msg: this.$t('Group'),
          }) as string).toLowerCase(),
          err: formatApiError(this.$i18n, err),
        }) as string,
        {
          icon: 'mdi-alert',
        }
      );
    }
  }

  private copyGroup() {
    const copyGroup = this.groups!.get(this.copyGroupId)!;
    this.editingGroup = {
      supplierId: copyGroup.supplierId,
      name: '',
      permissions: [],
      isInactive: copyGroup.isInactive,
      ticketPermissions: [],
      bookingPermissions: [],
      adminPermissions: [],
      isActive: !copyGroup.isInactive,
    };
    this.editingGroup.permissions = copyGroup.permissions;
    const ticketPermissions: TicketPermission[] = [];
    const bookingPermissions: BookingPermission[] = [];
    const adminPermissions: AdminPermission[] = [];
    this.editingGroup.permissions.forEach(p => {
      !!p.ticketPermission && ticketPermissions.push(p.ticketPermission);
      !!p.bookingPermission && bookingPermissions.push(p.bookingPermission);
      !!p.adminPermission && adminPermissions.push(p.adminPermission);
    });
    this.editingGroup.ticketPermissions = ticketPermissions;
    this.editingGroup.bookingPermissions = bookingPermissions;
    adminPermissions.forEach(p => {
      p.targetGroupId = p.targetGroupId || '';
    });
    this.editingGroup.adminPermissions = adminPermissions;
  }

  private reset() {
    const supplierId = (JSON.parse(UserModule.token) as IUserTokenData)
      .supplierId;
    this.editingGroup = {
      supplierId: supplierId,
      name: '',
      permissions: [],
      isInactive: true,
      isActive: false,
      ticketPermissions: [],
      bookingPermissions: [],
      adminPermissions: [],
    };
  }

  private async createGroup() {
    this.loading = true;
    AppModule.StartProgress();

    try {
      const newGroup = JSON.parse(
        JSON.stringify(this.editingGroup)
      ) as IPutGroupRequest & WithPermissions & WithIsActive;
      const permissions: Permission[] = [];
      newGroup.ticketPermissions.forEach(p =>
        permissions.push({
          ticketPermission: p,
        })
      );
      newGroup.bookingPermissions.forEach(p =>
        permissions.push({
          bookingPermission: p,
        })
      );
      newGroup.adminPermissions.forEach(p => {
        if (
          (p.action as AdminPermissionAllowedActionEnum) !==
          AdminPermissionAllowedActionEnum.GROUP_ADMIN
        ) {
          permissions.push({
            adminPermission: {
              action: p.action,
            },
          });
        } else {
          permissions.push({
            adminPermission: {
              action: p.action,
              targetGroupId: p.targetGroupId,
            },
          });
        }
      });
      newGroup.permissions = permissions;

      const req: IPutGroupRequest = {
        supplierId: newGroup.supplierId,
        name: newGroup.name,
        permissions: newGroup.permissions,
        isInactive: newGroup.isInactive,
      };

      const res = await putGroup(req);
      await this.postCreate(res.id);
    } catch (err) {
      this.$toast.error(
        this.$t('Failed to {do} due to {err}', {
          do: (this.translateRouteTitle(
            this.$route.meta.title
          ) as string).toLowerCase(),
          err: formatApiError(this.$i18n, err),
        }) as string,
        {
          icon: 'mdi-alert',
        }
      );
      await this.postCreate();
    }

    AppModule.StopProgress();
    this.loading = false;
  }

  private async postCreate(groupId?: string) {
    if (this.redirectAfterCreate && groupId) {
      this.$nextTick(() => {
        this.$router.push(`/group/${groupId}`);
      });
    } else {
      this.reset();
      await this.fetchData(true);

      if (groupId) {
        this.$toast.success(
          this.$t('Succeeded {do}', {
            do: (this.translateRouteTitle(
              this.$route.meta.title
            ) as string).toLowerCase(),
          }) as string,
          {
            icon: this.$route.meta.icon,
          }
        );
      }
    }
  }

  private addEditingTicketPermission() {
    this.editingGroup.ticketPermissions.push({
      ticketId: '',
      action: TicketPermissionAllowedActionEnum.UNKNOWN,
    });
  }

  private removeEditingTicketPermission(ticketPermissionIndex: number) {
    this.editingGroup.ticketPermissions.splice(ticketPermissionIndex, 1);
  }

  private addEditingBookingPermission() {
    this.editingGroup.bookingPermissions.push({
      action: BookingPermissionAllowedActionEnum.UNKNOWN,
    });
  }

  private removeEditingBookingPermission(bookingPermissionIndex: number) {
    this.editingGroup.bookingPermissions.splice(bookingPermissionIndex, 1);
  }

  private addEditingAdminPermission() {
    this.editingGroup.adminPermissions.push({
      action: AdminPermissionAllowedActionEnum.UNKNOWN,
      targetGroupId: '',
    });
  }

  private removeEditingAdminPermission(adminPermissionIndex: number) {
    this.editingGroup.adminPermissions.splice(adminPermissionIndex, 1);
  }

  private isGroupAdminPermission(adminPermission: AdminPermission) {
    return (
      (adminPermission.action as AdminPermissionAllowedActionEnum) ===
      AdminPermissionAllowedActionEnum.GROUP_ADMIN
    );
  }

  private getGroupName(groupId?: string) {
    return groupId && this.groups.has(groupId)
      ? this.groups.get(groupId)!.name
      : '';
  }

  private getRequiredRules(
    msg: any,
    exception: (v: any) => boolean = () => false
  ) {
    return [
      (v: string) =>
        exception(v) ||
        !!v ||
        (this.$t('{msg} is required', {
          msg: this.$te(msg) ? this.$t(msg) : msg,
        }) as string),
    ];
  }

  private translateRouteTitle(title: string) {
    return translateRouteTitle(this.$i18n, title);
  }
}
